import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn.js';

/**
 * A dropdown that cannot be clipped by the grid it lives in.
 *
 * `EntryGrid` wraps its table in `overflow-x-auto`, and a CSS overflow that is
 * not `visible` on one axis clips the other as well — so a list positioned
 * `absolute` inside a voucher line is cut off at the row it sits in. Both the
 * account picker and the invoice scan bar hit that, so the escape hatch lives
 * here rather than twice: the list renders into `document.body` as
 * `position: fixed`, anchored to the control's rect and re-measured while open.
 *
 * `capture: true` on the scroll listener is the load-bearing part. The events
 * that move a voucher row come from the grid's own scroller, not from the
 * window, and they do not bubble.
 */

/** How far a list may grow before it scrolls internally. */
export const MAX_LIST_HEIGHT = 288;

export interface Anchor {
  left: number;
  top: number;
  width: number;
  height: number;
  /** True when there is more room above the control than below it. */
  above: boolean;
}

export function useAnchor(open: boolean) {
  const ref = useRef<HTMLInputElement>(null);
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  const measure = useCallback(() => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - rect.bottom;
    setAnchor({
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
      above: below < Math.min(MAX_LIST_HEIGHT, 200) && rect.top > below,
    });
  }, []);

  // Before paint, so the list never appears at the previous anchor for a frame
  // when the control moves between openings.
  useLayoutEffect(() => {
    if (open) measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const reposition = () => measure();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, measure]);

  return { ref, anchor, measure };
}

export function AnchoredList({
  anchor,
  id,
  listRef,
  minWidth = 260,
  children,
}: {
  anchor: Anchor;
  id: string;
  listRef: React.RefObject<HTMLDivElement | null>;
  minWidth?: number;
  children: ReactNode;
}) {
  return createPortal(
    <div
      ref={listRef}
      id={id}
      role="listbox"
      data-noprint="1"
      // `fixed` against the viewport: the anchor rect is already in viewport
      // coordinates, so no scroll offset is added anywhere.
      style={{
        position: 'fixed',
        left: anchor.left,
        width: Math.max(anchor.width, minWidth),
        ...(anchor.above
          ? { bottom: window.innerHeight - anchor.top + 4 }
          : { top: anchor.top + anchor.height + 4 }),
        maxHeight: MAX_LIST_HEIGHT,
        zIndex: 90,
      }}
      className={cn(
        'overflow-y-auto rounded-[var(--radius-sm)] border border-[var(--line)]',
        'bg-[var(--surface)] py-1 shadow-[var(--shadow-2,0_12px_28px_rgba(0,0,0,0.18))]',
      )}
      // Keeps focus in the input so the caret and the filter survive a click on
      // a row.
      onMouseDown={(event) => event.preventDefault()}
    >
      {children}
    </div>,
    document.body,
  );
}
